import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import Popover from 'modules/core/components/Popover'
import type { Attachment } from 'modules/core/components/TetherComponent/types'
import i18n from 'modules/core/lib/i18n'

import LeaveTypeSelector from 'modules/timeoff/components/LeaveTypeSelector'
import LeaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import CreateLeaveLink from './CreateLeaveLink'

interface Props {
  label: string
  secondaryLabel?: React.ComponentProps<typeof Text>['secondaryLabel']
  description?: string
  leaveTypes: LeaveType[]
  onSubmit?: (arg0: string[]) => void
  attachment?: Attachment
  selectedIds: string[]
  multiselect?: boolean
  readonly?: boolean
  mandatory?: boolean
  showCreateLeave: boolean
}

export const LeaveTypeSelectorInput = observer(
  ({
    label,
    secondaryLabel,
    description,
    selectedIds,
    leaveTypes,
    onSubmit,
    attachment = 'top center',
    multiselect = true,
    readonly = false,
    mandatory = false,
    showCreateLeave = false,
  }: Props) => {
    const [focused, setFocused] = React.useState(false)
    const [ids, setIds] = React.useState(selectedIds)

    React.useEffect(() => {
      if (selectedIds) {
        setIds(selectedIds)
      }
    }, [selectedIds])

    const count = ids.length

    const leaveType = LeaveTypes.find({
      id: ids[0],
    })?.name

    const textLabel = count
      ? i18n.tp('leave_type_selector.label', {
          count,
          leave_type: leaveType,
        })
      : ''

    const submitLabel = count
      ? i18n.tp('leave_type_selector.submit', {
          count,
        })
      : i18n.t('leave_type_selector.submit.zero')

    const onSubmited = () => {
      if (onSubmit) onSubmit(ids)

      setFocused(false)
    }

    const onSelect = (leaveTypeId: string | null | undefined) => {
      setIds(xor(ids, [leaveTypeId].filter(Boolean)))
    }

    const placeholder = mandatory
      ? i18n.t('leave_type_selector.label.zero')
      : i18n.t('leave_type_selector.label.zero_optional')

    return (
      <Popover
        attachment={attachment}
        open={focused}
        size="bigger"
        toggle={(val) => () => !readonly && setFocused(val)}
        onOutsideClick={onSubmited}
        focusTrapActive={false}
      >
        <Text
          label={label}
          secondaryLabel={secondaryLabel}
          description={description}
          readonly={readonly}
          field={
            {
              value: textLabel,
            } as any
          }
          select
          mandatory={mandatory}
          placeholder={placeholder}
        />
        <Box padding="s32" gap="s24">
          <LeaveTypeSelector
            leaveTypes={leaveTypes}
            onSelect={onSelect}
            onSelectMultiple={setIds}
            selectedIds={ids}
            multiselect={multiselect}
          />
          {showCreateLeave && <CreateLeaveLink />}
          <Button onClick={onSubmited}>{submitLabel}</Button>
        </Box>
      </Popover>
    )
  }
)
